import React from 'react';

import { useNavigate } from 'react-router-dom';

import { reverse } from 'named-urls';

import EditIcon from '@mui/icons-material/Edit';

import { GridLegacy as Grid } from '@mui/material';

import { Button } from 'components/Button';

import { InspectionEntity, InspectionTaskLinkType } from 'utils/api/modules/workflows/workflows.types';
import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';
import { findLinkByEntity } from 'utils/api/modules/tasks/tasks.utils';
import { AuditLinkEntity } from 'utils/api/modules/entities/entities.types';
import { AuditTaskLinkExtraData } from 'utils/api/types';

import { useStore } from 'utils/hooks/store';

import UserRow from 'components/UserRow';

import routes from 'core/routes';

import { EntityModules, isRegisterEntityModule } from 'core/constants';

import { AuditQuestionDrawerTab } from 'components/Audits/AuditQuestionDrawer';
import { openQuestionParams } from 'components/Audits/questionDrawer';

import EditTaskProperties from 'containers/TaskDetails/EditTaskProperties';

import SessionUser from 'stores/Session/SessionUser';

import FormIcon from 'components/Icons/FormIcon';

import QuestionIcon from 'components/Icons/QuestionIcon';

import EntityDisplayValue from 'components/Entities/EntityDisplayValue/EntityDisplayValue';

import { useEntityDisplayValue } from 'components/Entities/EntityDisplayValue/utils';

import {
  StyledName,
  DescriptionField,
  RelatedToIncidentValue,
  StyledRedirect,
  TaskRecordContainer,
  TaskRecordRow,
  RecordRow,
  FirstColumn,
  SecondColumn,
  Title,
  HeaderGrid,
} from './styles';

import TaskDueDate from '../TaskDueDate';
import TaskStatusChip from '../TaskStatusChip';

const Row: React.FC<{ name: string; children: React.ReactNode; id: string }> = ({
  name,
  children,
  id,
}): React.ReactElement => (
  <TaskRecordRow id={id}>
    <RecordRow key={id}>
      <FirstColumn>{name}</FirstColumn>
      <SecondColumn>{children}</SecondColumn>
    </RecordRow>
  </TaskRecordRow>
);

const TaskProperties: React.FunctionComponent = (): React.ReactElement => {
  const { taskDetails, session } = useStore();

  const details = taskDetails.details as TaskListResponse;
  const navigate = useNavigate();

  const [editing, setEditing] = React.useState<boolean>(false);

  const { licenses, activeEntityModules, readOnly } = session.user as SessionUser;
  const auditsEnabled = activeEntityModules.includes(EntityModules.Audits);

  const navigateToForm = (event: React.MouseEvent): void => {
    if (licenses.workflows) {
      const inspectionLink = findLinkByEntity(details.links, InspectionEntity);

      const formId = inspectionLink?.instance.uuid as string;
      navigate(reverse(routes.dashboard.audits.forms.details, { formId }));
      event.stopPropagation();
    }
  };

  const navigateToQuestion = (event: React.MouseEvent): void => {
    if (licenses.workflows) {
      const inspectionLink = findLinkByEntity(details.links, InspectionEntity);

      if (!inspectionLink) return;
      const formId = inspectionLink.instance.uuid as string;
      const questionId = (inspectionLink?.extraData as InspectionTaskLinkType).questionId as string;
      const route = reverse(routes.dashboard.audits.forms.details, {
        formId,
      });
      navigate(`${route}?questionId=${questionId}`);
      event.stopPropagation();
    }
  };

  const inspectionLink = findLinkByEntity(details.links, InspectionEntity);
  const auditLink = auditsEnabled ? findLinkByEntity(details.links, AuditLinkEntity) : undefined;
  const auditExtraData = auditLink?.extraData as AuditTaskLinkExtraData | undefined;
  // The drawer only opens on a submitted audit's record, and only for a question still on its form
  // (a null label means an upgrade dropped it). Otherwise the question is named but not linked.
  const questionIsClickable = auditExtraData?.host === 'submission' && !!auditExtraData.question;
  const auditRoute = auditLink
    ? reverse(routes.dashboard.audits.forms.details, { formId: auditLink.instance.uuid as string })
    : '';

  const navigateToAudit = (event: React.MouseEvent): void => {
    navigate(auditRoute);
    event.stopPropagation();
  };

  const navigateToAuditQuestion = (event: React.MouseEvent): void => {
    if (!auditExtraData) return;
    navigate(
      `${auditRoute}?${openQuestionParams(
        new URLSearchParams(),
        auditExtraData.questionId,
        AuditQuestionDrawerTab.Tasks
      )}`
    );
    event.stopPropagation();
  };

  return (
    <>
      <HeaderGrid container alignItems="center">
        <Grid item xs={10} container alignContent="flex-start">
          <Title>Task Details</Title>
        </Grid>
        <Grid item xs={2} container justifyContent="end">
          {!editing && !readOnly && (
            <Button
              size="small"
              variant="text"
              onClick={() => setEditing((prevState) => !prevState)}
              sx={{ padding: 0, fontSize: '14px' }}
            >
              <EditIcon sx={{ width: 20, height: 20, marginRight: '8px' }} />
              Edit
            </Button>
          )}
        </Grid>
      </HeaderGrid>
      <TaskRecordContainer $editing={editing}>
        {editing ? (
          <EditTaskProperties setEditing={setEditing} />
        ) : (
          <>
            <Row name="Name" id="task-name-value">
              <StyledName id="task-name-value">{details.name}</StyledName>
            </Row>

            <Row name="Audit" id="related-to-label">
              {inspectionLink && (
                <>
                  <StyledRedirect
                    onClick={navigateToForm}
                    role="link"
                    style={{
                      marginBottom: '6px',
                    }}
                    $isClickable={licenses.workflows || false}
                  >
                    <FormIcon />
                    <RelatedToIncidentValue id="incident-related-to-incident">
                      {inspectionLink.instance.name}
                    </RelatedToIncidentValue>
                  </StyledRedirect>

                  {inspectionLink.extraData ? (
                    <StyledRedirect onClick={navigateToQuestion} role="link" $isClickable={licenses.workflows || false}>
                      <QuestionIcon />
                      <RelatedToIncidentValue id="incident-related-to-incident">
                        {(inspectionLink.extraData as InspectionTaskLinkType).question}
                      </RelatedToIncidentValue>
                    </StyledRedirect>
                  ) : null}
                </>
              )}

              {auditLink && (
                <>
                  <StyledRedirect
                    onClick={navigateToAudit}
                    role="link"
                    style={{
                      marginBottom: '6px',
                    }}
                    $isClickable
                    id="task-audit-name"
                  >
                    <FormIcon />
                    <RelatedToIncidentValue>{auditLink.instance.name}</RelatedToIncidentValue>
                  </StyledRedirect>

                  {auditExtraData ? (
                    <StyledRedirect
                      onClick={questionIsClickable ? navigateToAuditQuestion : undefined}
                      role={questionIsClickable ? 'link' : undefined}
                      $isClickable={questionIsClickable}
                      id="task-audit-question"
                    >
                      <QuestionIcon />
                      <RelatedToIncidentValue>
                        {auditExtraData.question || auditExtraData.questionId}
                      </RelatedToIncidentValue>
                    </StyledRedirect>
                  ) : null}
                </>
              )}

              {!inspectionLink && !auditLink && '-'}
            </Row>

            {activeEntityModules.filter(isRegisterEntityModule).map((module) => {
              const { path, entityLink, singular } = useEntityDisplayValue(module, details.links);
              return (
                <Row name={singular} id={`related-${singular}-label`}>
                  {entityLink ? <EntityDisplayValue link={entityLink.instance.name} path={path} /> : '-'}
                </Row>
              );
            })}

            <Row name="Description" id="description-label">
              {details.description ? (
                <DescriptionField id="task-description">
                  {details.description.split('\n').map((str, index) => (
                    <p key={`${str}_${index}`}>{str}</p>
                  ))}
                </DescriptionField>
              ) : (
                '-'
              )}
            </Row>
            <Row name="Assigned to" id="assigned-to-label">
              {details.assigned ? <UserRow user={details.assigned} /> : '-'}
            </Row>
            <Row name="Status" id="status-label">
              <TaskStatusChip status={details.status} />
            </Row>
            <Row name="Due date" id="due-date-label">
              {details.dueDate ? <TaskDueDate date={details.dueDate} status={details.status} /> : '-'}
            </Row>
          </>
        )}
      </TaskRecordContainer>
    </>
  );
};

export default TaskProperties;
